// ACCTEXPORT1 (docs/build-briefs/ACCTEXPORT1-quickbooks-connection.md; DEC-514; design/style-guide.md sections 5, 8, 9, 10, 11, 12).
// Settings > Connections > QuickBooks: one card per location with the connection (Connect or Disconnect), the
// six accounts each journal line goes to (picked from the company's own list, never typed), and the last two weeks of closed days
// with what was sent, what is waiting and, in plain words, why anything is stuck, with Send or Retry on the row that can use it.
//
// The location is chosen from the locations the server lists (this device's first), never typed. QuickBooks is connected on
// QuickBooks's own page: the server returns its address once and the browser follows it (https, or http on this machine only);
// no token, code or company id is ever here. Dark while the feature is off (every read answers 404 and the card stays hidden). Admin
// surface: pointer and keyboard are fine and every control is 44px or taller. All text via textContent.

import {
  getAccountingAccounts,
  getAccountingStatus,
  listTenantLocations,
  postAccountingConnectionStart,
  postAccountingDisconnect,
  postAccountingSendDay,
  putAccountingMapping,
  type AccountingAccountDto,
  type AccountingStatusDto,
  type TenantLocationNameDto,
} from "./api.ts";
import {
  ARRIVAL_TEXT,
  connectionView,
  dayRows,
  type AccountingArrival,
  type Chip,
} from "./accountingModel.ts";
import { hoverHelp } from "./hoverHelp.ts";
import { sortByName } from "./nameSort.ts";

export interface AccountingSectionDeps {
  apiBaseUrl: () => string;
  tenantId: () => string;
  /** The session holds admin.tenant_settings.view (the read of this card). */
  canView: () => boolean;
  /** The session holds admin.location_settings.manage (connect, disconnect, the accounts, Send and Retry). */
  canManage: () => boolean;
  /** The location this device belongs to, when it is set up as one. */
  deviceLocation: () => { id: string; name: string } | undefined;
  /** True while the session this section serves is still the live one. */
  isCurrent: () => boolean;
  /** Takes the browser to QuickBooks's consent page. */
  navigate: (url: string) => void;
}

export interface AccountingSection {
  refresh(): Promise<void>;
  /** The browser came back from QuickBooks with one outcome word. */
  handleArrival(arrival: AccountingArrival): Promise<void>;
  reset(): void;
  /** True while the card is on screen: the feature is on and this person may see it. */
  isOffered(): boolean;
}

function node<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string | null,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tag);
  if (className !== null) created.className = className;
  if (text !== undefined) created.textContent = text;
  return created;
}

function messageOf(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === "string" && message.length > 0
    ? message
    : "Something went wrong. Try again.";
}

function chipNode(chip: Chip, testid: string): HTMLElement {
  const element = node("span", `fl-chip fl-chip-${chip.tone}`);
  element.dataset["testid"] = testid;
  if (chip.symbol !== "") element.append(node("span", "fl-chip-symbol", chip.symbol));
  element.append(document.createTextNode(chip.label));
  return element;
}

/** The consent address is followed only when it is https, or http on this machine (the local test fake). */
export function isFollowableConsentUrl(raw: string): boolean {
  try {
    const url = new URL(raw);
    const local = url.hostname === "localhost" || url.hostname === "127.0.0.1";
    return url.protocol === "https:" || (url.protocol === "http:" && local);
  } catch {
    return false;
  }
}

const ID_PREFIX = "admin-accounting";

export function mountAccountingSection(
  root: HTMLElement,
  deps: AccountingSectionDeps,
): AccountingSection {
  let locations: { id: string; name: string }[] = [];
  let locationId: string | undefined;
  let status: AccountingStatusDto | undefined;
  let accounts: AccountingAccountDto[] | undefined;
  let draft: Record<string, string> = {};
  let statusLine = "";
  let savedLine = "";
  let errorLine = "";
  let confirmingDisconnect = false;
  let busy = false;
  let sendingDay: string | undefined;
  let loadSequence = 0;
  root.hidden = true;

  function reset(): void {
    loadSequence += 1;
    locations = [];
    locationId = undefined;
    status = undefined;
    accounts = undefined;
    draft = {};
    statusLine = "";
    savedLine = "";
    errorLine = "";
    confirmingDisconnect = false;
    busy = false;
    sendingDay = undefined;
    root.replaceChildren();
    root.hidden = true;
  }

  async function loadLocations(): Promise<void> {
    const listed = await listTenantLocations(deps.apiBaseUrl(), deps.tenantId());
    const device = deps.deviceLocation();
    if (listed.status === 200 && Array.isArray(listed.body.locations)) {
      const found = listed.body.locations as TenantLocationNameDto[];
      locations = found.map((siteLocation) => ({ id: siteLocation.id, name: siteLocation.name }));
      const preferred =
        (device !== undefined &&
          locations.find((siteLocation) => siteLocation.id === device.id)?.id) ||
        found.find((siteLocation) => siteLocation.isMain)?.id ||
        locations[0]?.id;
      locationId =
        locationId !== undefined && locations.some((siteLocation) => siteLocation.id === locationId)
          ? locationId
          : preferred;
      return;
    }
    locations = device === undefined ? [] : [device];
    locationId = device?.id;
  }

  /** Reads the card's data. A 404 (feature off) or 403 hides the card and says nothing. */
  async function read(): Promise<void> {
    if (!deps.canView()) {
      reset();
      return;
    }
    const sequence = (loadSequence += 1);
    if (locations.length === 0) {
      await loadLocations();
      if (sequence !== loadSequence || !deps.isCurrent()) return;
    }
    if (locationId === undefined) {
      reset();
      return;
    }
    const result = await getAccountingStatus(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (sequence !== loadSequence || !deps.isCurrent()) return;
    if (result.status === 404 || result.status === 403) {
      const keep = locations;
      reset();
      locations = keep;
      return;
    }
    if (result.status !== 200 || result.body.enabled !== true) {
      status = undefined;
      errorLine = "Could not load Accounting. Try again in a minute.";
      render();
      return;
    }
    status = result.body;
    draft = {};
    for (const line of status.mapping.lines)
      if (line.accountId !== null) draft[line.key] = line.accountId;
    if (status.connection.status === "connected" && deps.canManage() && accounts === undefined) {
      const list = await getAccountingAccounts(deps.apiBaseUrl(), deps.tenantId(), locationId);
      if (sequence !== loadSequence || !deps.isCurrent()) return;
      if (list.status === 200 && Array.isArray(list.body.accounts)) accounts = list.body.accounts;
    }
    if (status.connection.status !== "connected") accounts = undefined;
    render();
  }

  function render(): void {
    root.replaceChildren();
    root.hidden = false;
    const card = node("section", "bd-card");
    card.dataset["testid"] = `${ID_PREFIX}-card`;
    const head = node("div", "bd-card-head");
    const title = node("h3", "bd-card-title", "Accounting (QuickBooks)");
    hoverHelp(
      title,
      "Sends each closed day to your QuickBooks company as a journal entry.",
      "You choose which QuickBooks account each line goes to.",
      "Tips collected go to your Tips payable account.",
    );
    head.append(title);
    const view = status === undefined ? undefined : connectionView(status.connection);
    if (view !== undefined) head.append(chipNode(view.chip, `${ID_PREFIX}-connection-chip`));
    card.append(head);

    if (locations.length > 1) {
      const chips = node("div", "rt-locations");
      chips.setAttribute("role", "group");
      chips.setAttribute("aria-label", "Which location");
      for (const siteLocation of sortByName(locations, (listed) => listed.name)) {
        const chip = node("button", "tablets-chip", siteLocation.name);
        chip.type = "button";
        chip.dataset["testid"] = `${ID_PREFIX}-location`;
        chip.setAttribute("aria-pressed", String(siteLocation.id === locationId));
        chip.addEventListener("click", () => {
          if (siteLocation.id === locationId || busy) return;
          locationId = siteLocation.id;
          errorLine = "";
          statusLine = "";
          accounts = undefined;
          void read();
        });
        chips.append(chip);
      }
      card.append(chips);
    }

    if (statusLine !== "") {
      const line = node("p", "cp-status-line", statusLine);
      line.setAttribute("role", "status");
      line.dataset["testid"] = `${ID_PREFIX}-status`;
      card.append(line);
    }

    if (status !== undefined && view !== undefined) {
      card.append(renderConnection(view));
      if (view.connected) {
        card.append(renderAccounts(status));
        card.append(renderDays(status));
      }
    }

    if (errorLine !== "") {
      const error = node("p", "admin-error", errorLine);
      error.setAttribute("role", "alert");
      error.dataset["testid"] = `${ID_PREFIX}-error`;
      card.append(error);
      if (status === undefined) {
        const again = node("button", "btn-secondary btn-compact", "Try again");
        again.type = "button";
        again.dataset["testid"] = `${ID_PREFIX}-retry-load`;
        again.addEventListener("click", () => {
          errorLine = "";
          void read();
        });
        card.append(again);
      }
    }
    root.append(card);
  }

  function renderConnection(view: ReturnType<typeof connectionView>): HTMLElement {
    const wrap = node("div", null);
    const row = node("div", "svc-row");
    const text = node("div", "svc-text");
    text.append(node("span", "svc-title", view.connected ? "QuickBooks company" : "Connection"));
    const caption = node("span", "svc-sub", view.caption);
    caption.dataset["testid"] = `${ID_PREFIX}-caption`;
    text.append(caption);
    row.append(text);
    if (deps.canManage()) {
      const button = node(
        "button",
        view.action.kind === "disconnect" ? "btn-secondary btn-compact" : "btn-primary btn-compact",
        view.action.label,
      );
      button.type = "button";
      button.dataset["testid"] = `${ID_PREFIX}-${view.action.kind}`;
      button.disabled = busy;
      button.addEventListener("click", () => {
        if (view.action.kind === "disconnect") {
          confirmingDisconnect = !confirmingDisconnect;
          render();
          return;
        }
        void connect();
      });
      row.append(button);
    } else {
      row.append(node("span", "cp-tag", "View only"));
    }
    wrap.append(row);
    if (confirmingDisconnect && view.connected) {
      const panel = node("div", "ba-confirm");
      panel.dataset["testid"] = `${ID_PREFIX}-disconnect-confirm`;
      panel.append(
        node(
          "p",
          "cp-fact",
          "Disconnecting stops new days from being sent. Days already in QuickBooks stay there.",
        ),
      );
      const actions = node("div", "ba-confirm-actions");
      const cancel = node("button", "btn-secondary btn-compact", "Cancel");
      cancel.type = "button";
      cancel.dataset["testid"] = `${ID_PREFIX}-disconnect-cancel`;
      cancel.addEventListener("click", () => {
        confirmingDisconnect = false;
        render();
      });
      const go = node("button", "btn-danger btn-compact", "Disconnect");
      go.type = "button";
      go.dataset["testid"] = `${ID_PREFIX}-disconnect-confirm-button`;
      go.disabled = busy;
      go.addEventListener("click", () => void disconnect());
      actions.append(cancel, go);
      panel.append(actions);
      wrap.append(panel);
    }
    return wrap;
  }

  function renderAccounts(current: AccountingStatusDto): HTMLElement {
    const wrap = node("div", "cp-section");
    wrap.append(node("h4", "section-title", "Accounts"));
    const mayManage = deps.canManage();
    for (const line of current.mapping.lines) {
      const row = node("div", "svc-row");
      const text = node("div", "svc-text");
      const labelId = `${ID_PREFIX}-${line.key}`;
      const label = node("label", "svc-title", line.label);
      label.htmlFor = labelId;
      const help = node("span", "svc-sub", line.hint);
      help.id = `${labelId}-help`;
      text.append(label, help);
      row.append(text);
      if (mayManage && accounts !== undefined) {
        const select = node("select", "field-input");
        select.id = labelId;
        select.dataset["testid"] = labelId;
        select.setAttribute("aria-describedby", `${labelId}-help`);
        select.disabled = busy;
        const none = node("option", null, "Choose an account");
        none.value = "";
        select.append(none);
        for (const account of accounts) {
          const option = node("option", null, account.name);
          option.value = account.id;
          select.append(option);
        }
        select.value = draft[line.key] ?? "";
        select.addEventListener("change", () => {
          if (select.value === "") delete draft[line.key];
          else draft[line.key] = select.value;
          savedLine = "";
        });
        row.append(select);
      } else {
        const name = node("span", "svc-sub", line.accountName ?? "Not chosen yet");
        name.dataset["testid"] = `${labelId}-name`;
        row.append(name);
      }
      wrap.append(row);
    }
    if (mayManage && accounts !== undefined) {
      const actions = node("div", "admin-settings-save-row");
      const save = node("button", "btn-primary btn-compact", "Save accounts");
      save.type = "button";
      save.dataset["testid"] = `${ID_PREFIX}-save`;
      save.disabled = busy;
      save.addEventListener("click", () => void saveAccounts());
      const saved = node("span", "admin-settings-save-status", savedLine);
      saved.setAttribute("role", "status");
      saved.dataset["testid"] = `${ID_PREFIX}-saved`;
      actions.append(save, saved);
      wrap.append(actions);
    }
    return wrap;
  }

  function renderDays(current: AccountingStatusDto): HTMLElement {
    const wrap = node("div", "cp-section");
    wrap.append(node("h4", "section-title", "Recent days"));
    if (current.days.length === 0) {
      const empty = node("div", "fl-empty");
      empty.dataset["testid"] = `${ID_PREFIX}-empty`;
      empty.append(
        node("b", null, "No closed days yet."),
        document.createTextNode(" Each day shows here once it is finalized."),
      );
      wrap.append(empty);
      return wrap;
    }
    const frame = node("div", "fl-wrap");
    const table = node("table", "fl-table");
    table.dataset["testid"] = `${ID_PREFIX}-days`;
    table.setAttribute("aria-label", "Recent days sent to QuickBooks");
    const headRow = node("tr", null);
    const columns: { text: string; numeric: boolean; width: string }[] = [
      { text: "Day", numeric: false, width: "16%" },
      { text: "Sales", numeric: true, width: "14%" },
      { text: "Tax", numeric: true, width: "12%" },
      { text: "Tips", numeric: true, width: "12%" },
      { text: "Status", numeric: false, width: "34%" },
      { text: "", numeric: false, width: "12%" },
    ];
    for (const column of columns) {
      const th = node("th", column.numeric ? "fl-plain fl-num" : "fl-plain", column.text);
      th.scope = "col";
      th.style.width = column.width;
      headRow.append(th);
    }
    const thead = node("thead", null);
    thead.append(headRow);
    const body = node("tbody", null);
    for (const row of dayRows(current.days, deps.canManage())) {
      const tr = node("tr", null);
      tr.dataset["testid"] = `${ID_PREFIX}-day`;
      tr.dataset["businessDate"] = row.businessDate;
      tr.append(node("td", null, row.day));
      tr.append(
        node("td", "fl-num", row.sales),
        node("td", "fl-num", row.tax),
        node("td", "fl-num", row.tips),
      );
      const statusCell = node("td", "fl-wrap-cell");
      statusCell.append(chipNode(row.chip, `${ID_PREFIX}-day-status`));
      if (row.detail !== "") {
        const detail = node("div", "svc-sub", row.detail);
        detail.dataset["testid"] = `${ID_PREFIX}-day-detail`;
        statusCell.append(detail);
      }
      tr.append(statusCell);
      const actionCell = node("td", "fl-num");
      if (row.action !== null) {
        const button = node("button", "btn-secondary btn-compact", row.action.label);
        button.type = "button";
        button.dataset["testid"] = `${ID_PREFIX}-day-send`;
        button.disabled = busy || sendingDay !== undefined;
        button.addEventListener("click", () => void sendDay(row.businessDate));
        actionCell.append(button);
      }
      tr.append(actionCell);
      body.append(tr);
    }
    table.append(thead, body);
    frame.append(table);
    wrap.append(frame);
    return wrap;
  }

  async function connect(): Promise<void> {
    if (busy || locationId === undefined) return;
    busy = true;
    errorLine = "";
    statusLine = "";
    render();
    const result = await postAccountingConnectionStart(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
    );
    if (!deps.isCurrent()) return;
    busy = false;
    const url = result.body.authorizationUrl;
    if (result.status === 200 && typeof url === "string" && isFollowableConsentUrl(url)) {
      deps.navigate(url);
      return;
    }
    errorLine =
      result.status === 200 ? "That link is not valid. Try again." : messageOf(result.body);
    render();
  }

  async function disconnect(): Promise<void> {
    if (busy || locationId === undefined) return;
    busy = true;
    errorLine = "";
    render();
    const result = await postAccountingDisconnect(deps.apiBaseUrl(), deps.tenantId(), locationId);
    if (!deps.isCurrent()) return;
    busy = false;
    confirmingDisconnect = false;
    if (result.status !== 200) {
      errorLine = messageOf(result.body);
      render();
      return;
    }
    accounts = undefined;
    await read();
    statusLine =
      result.body.revoked === false
        ? "Disconnected here. QuickBooks did not confirm, so remove the app in QuickBooks too."
        : "Disconnected.";
    render();
  }

  async function saveAccounts(): Promise<void> {
    if (busy || locationId === undefined || status === undefined) return;
    if (Object.keys(draft).length === 0) {
      errorLine = "Choose an account for at least one line.";
      render();
      return;
    }
    busy = true;
    errorLine = "";
    savedLine = "";
    render();
    const result = await putAccountingMapping(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
      draft,
    );
    if (!deps.isCurrent()) return;
    busy = false;
    if (result.status !== 200) {
      errorLine = messageOf(result.body);
      render();
      return;
    }
    await read();
    savedLine = "Saved";
    render();
  }

  async function sendDay(businessDate: string): Promise<void> {
    if (busy || sendingDay !== undefined || locationId === undefined) return;
    sendingDay = businessDate;
    errorLine = "";
    render();
    const result = await postAccountingSendDay(
      deps.apiBaseUrl(),
      deps.tenantId(),
      locationId,
      businessDate,
    );
    if (!deps.isCurrent()) return;
    sendingDay = undefined;
    if (result.status !== 200) errorLine = messageOf(result.body);
    await read();
    if (errorLine !== "") render();
  }

  return {
    async refresh() {
      statusLine = "";
      savedLine = "";
      await read();
    },
    async handleArrival(arrival: AccountingArrival) {
      await read();
      if (status === undefined) return;
      statusLine = ARRIVAL_TEXT[arrival];
      render();
    },
    reset,
    isOffered: () => !root.hidden,
  };
}
